import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  X, RefreshCw, User, Settings, HelpCircle, LogOut, Wallet, Briefcase,
  Plus, FileText, ShieldCheck, Star, BookOpen, Mail, Phone, MapPin,
  ChevronRight, Facebook, Twitter, Instagram, Linkedin, LogIn, Heart,
} from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { useMobileNav } from '../../contexts/MobileNavContext';
import LanguageToggle from '../LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABEL = { client: 'Client', provider: 'Provider', admin: 'Admin' };

// Role-based accent colors (Client = green, Provider = blue, Admin = orange).
const ROLE_COLORS = {
  client: { solid: 'bg-green-600 text-white border-green-600', outline: 'bg-white text-green-700 border-green-300 active:bg-green-50' },
  provider: { solid: 'bg-blue-600 text-white border-blue-600', outline: 'bg-white text-blue-700 border-blue-300 active:bg-blue-50' },
  admin: { solid: 'bg-orange-500 text-white border-orange-500', outline: 'bg-white text-orange-700 border-orange-300 active:bg-orange-50' },
};

const Row = ({ icon: Icon, label, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-5 py-3.5 active:bg-gray-50 transition ${danger ? 'text-red-600' : 'text-gray-800'}`}
    data-testid={`drawer-link-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
  >
    <span className="flex items-center gap-3">
      <Icon className="w-5 h-5" strokeWidth={2} />
      <span className="text-[15px] font-medium">{label}</span>
    </span>
    {!danger && <ChevronRight className="w-4 h-4 text-gray-300" />}
  </button>
);

/**
 * Native app slide-in menu drawer. Holds the role switcher, role-specific links,
 * account/settings, "Become a Provider", and the company/footer information that
 * the website shows in its footer (requirement b). Native app only.
 */
export const MobileMenuDrawer = ({ onLogout }) => {
  const { user, switchRole, logout } = useUser();
  const { menuOpen, setMenuOpen } = useMobileNav();
  const navigate = useNavigate();
  const [info, setInfo] = useState({});
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    axios.get(`${API}/public/company-info`).then((r) => setInfo(r.data || {})).catch(() => {});
  }, [menuOpen]);

  const effectiveRole = user?.active_role || user?.role;
  // Mirror the website's role options (Navbar.getAvailableRoles) so owners can
  // switch Client / Provider / Admin from the mobile drawer too.
  const availableRoles = (() => {
    if (!user) return [];
    if (user.available_roles && user.available_roles.length > 0) return user.available_roles;
    const roles = [user.role];
    if (user.is_owner && !roles.includes('admin')) roles.push('admin');
    if (!roles.includes('client')) roles.push('client');
    if (!roles.includes('provider')) roles.push('provider');
    return [...new Set(roles)];
  })();
  const canBecomeProvider = user && !availableRoles.includes('provider') && effectiveRole !== 'admin' && !user.is_owner;

  const go = (path) => { setMenuOpen(false); navigate(path); };

  const handleSwitch = async (role) => {
    if (role === effectiveRole || switching) return;
    setSwitching(true);
    try {
      await switchRole(role);
      setMenuOpen(false);
      navigate('/dashboard');
    } catch (e) {
      /* noop */
    } finally {
      setSwitching(false);
    }
  };

  const handleBecomeProvider = () => {
    setMenuOpen(false);
    // Navbar (on the page) owns the Become-a-Provider modal; ask it to open.
    window.dispatchEvent(new Event('gj:openBecomeProvider'));
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    if (typeof onLogout === 'function') onLogout();
    window.dispatchEvent(new Event('gj:logout'));
    navigate('/');
  };

  // Role-specific quick links (the ones not already in the bottom tab bar).
  let quickLinks = [];
  if (effectiveRole === 'provider') {
    quickLinks = [
      { label: 'My Services', icon: Briefcase, path: '/my-services' },
      { label: 'My Bids', icon: FileText, path: '/my-bids' },
      { label: 'Earnings', icon: Wallet, path: '/earnings' },
    ];
  } else if (effectiveRole === 'admin') {
    quickLinks = [
      { label: 'Admin Dashboard', icon: ShieldCheck, path: '/admin' },
      ...(user?.is_owner ? [{ label: 'Owner Panel', icon: Briefcase, path: '/owner' }] : []),
    ];
  } else if (user) {
    quickLinks = [
      { label: 'Post a Job', icon: Plus, path: '/create-job' },
      { label: 'Payments', icon: Wallet, path: '/payments' },
      { label: 'Favorites', icon: Heart, path: '/favorites' },
    ];
  }

  const accountLinks = user
    ? [
        { label: 'Profile', icon: User, path: '/profile' },
        { label: 'Account Settings', icon: Settings, path: '/account-settings' },
        { label: 'Help & Support', icon: HelpCircle, path: '/help' },
      ]
    : [];

  // Company / footer info links (only those with content)
  const companyLinks = [
    info.about_us && { label: 'About Us', icon: BookOpen, path: '/about' },
    info.success_stories && { label: 'Success Stories', icon: Star, path: '/success-stories' },
    info.resources && { label: 'Provider Agreement', icon: FileText, path: '/resources' },
    info.privacy_policy && { label: 'Privacy Policy', icon: ShieldCheck, path: '/privacy-policy' },
    info.terms_of_service && { label: 'Terms of Service', icon: FileText, path: '/terms-of-service' },
    { label: 'Contact', icon: Mail, path: '/help' },
  ].filter(Boolean);

  const socials = [
    info.social_facebook && { icon: Facebook, url: info.social_facebook },
    info.social_twitter && { icon: Twitter, url: info.social_twitter },
    info.social_instagram && { icon: Instagram, url: info.social_instagram },
    info.social_linkedin && { icon: Linkedin, url: info.social_linkedin },
  ].filter(Boolean);

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 ${menuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMenuOpen(false)}
        data-testid="drawer-overlay"
      />
      {/* Panel */}
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-[86%] max-w-[380px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        data-testid="mobile-menu-drawer"
      >
        {/* Header — pad for the status bar so the close button aligns with the main header logo */}
        <div style={{ paddingTop: 'env(safe-area-inset-top)' }} className="border-b border-gray-100">
          <div className="flex items-center justify-between px-5 h-14">
            <span className="text-base font-semibold text-gray-900">Menu</span>
            <button onClick={() => setMenuOpen(false)} className="p-2 -mr-2 rounded-full hover:bg-gray-100 active:scale-95 transition" data-testid="drawer-close" aria-label="Close menu">
              <X className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* User block — profile tap on the left, language switch on the right */}
          {user ? (
            <div className="flex items-center gap-3 px-5 py-4" data-testid="drawer-user-row">
              <button onClick={() => go('/profile')} className="flex items-center gap-3 flex-1 min-w-0 text-left active:opacity-70 transition" data-testid="drawer-user">
                {user.avatar ? (
                  <img src={user.avatar.startsWith('/api') ? `${BACKEND_URL}${user.avatar}` : user.avatar} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-900 text-white flex items-center justify-center text-lg font-semibold shrink-0">
                    {user.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <span className="min-w-0">
                  <span className="block font-semibold text-gray-900 truncate">{user.name}</span>
                  <span className="block text-sm text-gray-500">{ROLE_LABEL[effectiveRole] || 'Member'}</span>
                </span>
              </button>
              <LanguageToggle className="shrink-0" />
            </div>
          ) : (
            <div className="px-5 py-4">
              <div className="flex justify-end mb-3">
                <LanguageToggle />
              </div>
              <div className="flex gap-3">
                <button onClick={() => go('/login')} className="flex-1 py-2.5 rounded-full bg-rose-600 text-white font-semibold text-sm flex items-center justify-center gap-2" data-testid="drawer-login">
                  <LogIn className="w-4 h-4" /> Log in
                </button>
                <button onClick={() => go('/register')} className="flex-1 py-2.5 rounded-full border border-gray-300 text-gray-800 font-semibold text-sm" data-testid="drawer-signup">
                  Sign up
                </button>
              </div>
            </div>
          )}

          {/* Role switcher */}
          {user && availableRoles.length > 1 && (
            <div className="px-5 pb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <RefreshCw className="w-3.5 h-3.5" /> Switch Role
              </p>
              <div className="flex flex-wrap gap-2" data-testid="drawer-role-switcher">
                {availableRoles.map((role) => {
                  const colors = ROLE_COLORS[role] || { solid: 'bg-rose-600 text-white border-rose-600', outline: 'bg-white text-gray-700 border-gray-200 active:bg-gray-50' };
                  return (
                    <button
                      key={role}
                      onClick={() => handleSwitch(role)}
                      disabled={switching}
                      className={`flex-1 min-w-[90px] py-2 rounded-xl text-sm font-semibold border transition ${effectiveRole === role ? colors.solid : colors.outline}`}
                      data-testid={`drawer-role-${role}`}
                    >
                      {ROLE_LABEL[role] || role}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick links */}
          {quickLinks.length > 0 && (
            <div className="border-t border-gray-100 py-1">
              {quickLinks.map((l) => <Row key={l.path} icon={l.icon} label={l.label} onClick={() => go(l.path)} />)}
            </div>
          )}

          {/* Become a provider */}
          {canBecomeProvider && (
            <div className="border-t border-gray-100 py-1">
              <Row icon={Briefcase} label="Become a Provider" onClick={handleBecomeProvider} />
            </div>
          )}

          {/* Account */}
          {accountLinks.length > 0 && (
            <div className="border-t border-gray-100 py-1">
              {accountLinks.map((l) => <Row key={l.path} icon={l.icon} label={l.label} onClick={() => go(l.path)} />)}
            </div>
          )}

          {/* Company / footer info */}
          <div className="border-t border-gray-100 py-1">
            <p className="px-5 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
              {info.company_name || 'GoodJob'}
            </p>
            {companyLinks.map((l) => <Row key={l.label} icon={l.icon} label={l.label} onClick={() => go(l.path)} />)}
          </div>

          {/* Contact + socials */}
          <div className="border-t border-gray-100 px-5 py-4 space-y-2 text-sm text-gray-500">
            {info.contact_email && info.show_email !== false && (
              <a href={`mailto:${info.contact_email}`} className="flex items-center gap-2" data-testid="drawer-email"><Mail className="w-4 h-4" /> {info.contact_email}</a>
            )}
            {info.contact_phone && info.show_phone !== false && (
              <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {info.contact_phone}</p>
            )}
            {info.address && info.show_address !== false && (
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {info.address}</p>
            )}
            {socials.length > 0 && (
              <div className="flex items-center gap-4 pt-2">
                {socials.map((s, i) => {
                  const Icon = s.icon;
                  return <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700"><Icon className="w-5 h-5" /></a>;
                })}
              </div>
            )}
            <p className="pt-2 text-xs text-gray-400">&copy; {new Date().getFullYear()} {info.company_name || 'GoodJob'}. All rights reserved.</p>
          </div>

          {/* Logout */}
          {user && (
            <div className="border-t border-gray-100 py-1 mb-2">
              <Row icon={LogOut} label="Log out" onClick={handleLogout} danger />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default MobileMenuDrawer;
